/**
 * ZDocumentTemplateFolders - Zod Validation Schemas for Document Template Folders API
 *
 * Endpoints: /api/document-template-folders/*
 * Controller: document-template/document-template-folders.controller.ts
 *
 * UpdateIcon is multipart/form-data (Body typed as FormData for the generated
 * client; the backend reads the file via FileInterceptor).
 */

import { z } from "zod";

import {
  CreateFolderBodySchema,
  FolderIconRemovedSchema,
  FolderIconUpdatedSchema,
  FolderMutationOkSchema,
  FolderPathVarsSchema,
  FolderSchema,
  UpdateFolderBodySchema
} from "../../common/folder.schema";
import { ApiSuccessSchema } from "../../common/response.schema";

// ═══════════════════════════════════════════════════════════════
// SHARED SCHEMAS
// ═══════════════════════════════════════════════════════════════

const DocumentTemplateFolderAssignmentSchema = z.object({
  documentTemplateId: z.string(),
  folderId: z.string()
});

export type DocumentTemplateFolderAssignment = z.infer<
  typeof DocumentTemplateFolderAssignmentSchema
>;

const DocumentTemplateFoldersGetAllResponseSchema = z.object({
  folders: z.array(FolderSchema),
  assignments: z.array(DocumentTemplateFolderAssignmentSchema)
});

const AssignDocumentTemplateBodySchema = z.object({
  // DocumentTemplate.id is a uuid column; the assignment table storing it is a bare
  // String with no foreign key, so an unvalidated id persists pointing at nothing.
  // The folderId sibling DOES have a foreign key, so a bad value there surfaces as a
  // Prisma error — a 500 where a 400 belongs. Only the body is tightened; the
  // response schema still describes rows written before this check existed.
  documentTemplateId: z.string().uuid(),
  folderId: z.string().uuid().nullable()
});

// ═══════════════════════════════════════════════════════════════
// ZDOCUMENT TEMPLATE FOLDERS MANIFEST
// ═══════════════════════════════════════════════════════════════

export const ZDocumentTemplateFolders = {
  /** GET /api/document-template-folders — all folders + template assignments. */
  GetAll: {
    method: "GET" as const,
    path: "/api/document-template-folders",
    Response: ApiSuccessSchema(DocumentTemplateFoldersGetAllResponseSchema)
  },

  /** POST /api/document-template-folders — create a folder. */
  Create: {
    method: "POST" as const,
    path: "/api/document-template-folders",
    Body: CreateFolderBodySchema,
    Response: ApiSuccessSchema(FolderSchema)
  },

  /** PATCH /api/document-template-folders/:id — rename or move a folder. */
  Update: {
    method: "PATCH" as const,
    path: "/api/document-template-folders/:id",
    PathVars: FolderPathVarsSchema,
    Body: UpdateFolderBodySchema,
    Response: ApiSuccessSchema(FolderSchema)
  },

  /** POST /api/document-template-folders/:id/updateIcon — multipart icon upload ("file" field). */
  UpdateIcon: {
    method: "POST" as const,
    path: "/api/document-template-folders/:id/updateIcon",
    PathVars: FolderPathVarsSchema,
    Body: z.custom<FormData>(),
    Response: ApiSuccessSchema(FolderIconUpdatedSchema)
  },

  /** POST /api/document-template-folders/:id/removeIcon — remove the folder icon. */
  RemoveIcon: {
    method: "POST" as const,
    path: "/api/document-template-folders/:id/removeIcon",
    PathVars: FolderPathVarsSchema,
    Response: ApiSuccessSchema(FolderIconRemovedSchema)
  },

  /** DELETE /api/document-template-folders/:id — delete a folder. */
  Delete: {
    method: "DELETE" as const,
    path: "/api/document-template-folders/:id",
    PathVars: FolderPathVarsSchema,
    Response: ApiSuccessSchema(FolderMutationOkSchema)
  },

  /** POST /api/document-template-folders/assign-item — assign a template (folderId null = root). */
  AssignItem: {
    method: "POST" as const,
    path: "/api/document-template-folders/assign-item",
    Body: AssignDocumentTemplateBodySchema,
    Response: ApiSuccessSchema(FolderMutationOkSchema)
  }
} as const;
